import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FileText, Loader2, Square } from "lucide-react";
import { cn } from "@/utils/helpers";
import ChatMarkdown from "@/app/routes/dashboard/components/ChatMarkdown";
import ChatModelProviderMark from "@/app/routes/dashboard/components/ChatModelProviderMark";
import { ChatTextWithUuidPills } from "@/app/routes/dashboard/components/chatUuidPills";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { KitModal } from "@/app/routes/dashboard/components/kit/Modal";
import { useChatModels } from "@/hooks/useChatModels";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import type { WorkshopDeployment } from "@/types/workshop.type";
import { isDeploymentRunBusy } from "@/types/workshop.type";
import {
  useWorkshopDeploymentArtifacts,
  useWorkshopDeploymentMessages,
  useWorkshopDeploymentRuns,
  useWorkshopDeployments,
} from "../api/useWorkshopDeployments";
import { formatAttribution, useViewerId } from "../attribution";
import WorkshopDialog from "./WorkshopDialog";

function formatWhen(iso: string | null): string {
  if (!iso) return "Not run yet";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatTokenCount(n: number | null | undefined): string {
  const v = n ?? 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 10_000) return `${Math.round(v / 1000)}k`;
  if (v >= 1_000) return `${(v / 1000).toFixed(1)}k`;
  return v.toLocaleString();
}

function RunStatusChip({ status }: { status: string | null }) {
  if (!status) {
    return (
      <span className="tt-label uppercase text-co-faint">
        never ran
      </span>
    );
  }
  const busy = isDeploymentRunBusy(status);
  const failed = status === "failed" || status === "error";
  // Green says one thing in Workshop: a run going now. A finished run is a
  // neutral state, a queued one waits (accent ink).
  const running = status === "running";
  return (
    <span
      className={cn(
        "tt-label inline-flex items-center gap-1 rounded-tag border px-1.5 py-0.5 uppercase leading-none",
        running && "border-co-live/40 text-co-live",
        busy && !running && "border-co-accent-border bg-co-accent-dim text-co-accent-ink",
        failed && "border-co-alert/40 bg-co-danger/10 text-co-alert-ink",
        !busy && !failed && "border-co-line2 text-co-muted",
      )}
    >
      {busy ? <Loader2 className="size-2 animate-spin" aria-hidden /> : null}
      {status}
    </span>
  );
}

/** One side of the conversation. Yours sits on the right, the agent's on the left. */
function TalkBubble({ text, side }: { text: string; side: "you" | "agent" }) {
  return (
    <div className={side === "you" ? "m7r-you" : "ws-agent-them"}>
      <div className="m7r-bubble">
        {side === "agent" ? (
          <div className="m7r-reply">
            <ChatMarkdown content={text} />
          </div>
        ) : (
          <ChatTextWithUuidPills text={text} />
        )}
      </div>
    </div>
  );
}

/**
 * What the agent was sent and what it replied, oldest first. The first message
 * is its objective (shown above), so the thread starts at its first reply.
 */
/** Reveal order on open: the header strip, then each section, then the footer, 30ms apart. */
const stagger = (i: number) => ({ "--i": i }) as React.CSSProperties;

function AgentConversation({
  deploymentId,
  busy,
  index,
}: {
  deploymentId: string;
  busy: boolean;
  index: number;
}) {
  const query = useWorkshopDeploymentMessages(deploymentId, true, busy);
  const messages = (query.data ?? []).slice(1);

  return (
    <section className="co-notif-in flex flex-col gap-2" style={stagger(index)}>
      <p className="tt-label font-semibold uppercase text-co-faint">
        Conversation
      </p>
      {query.isLoading ? (
        <div className="flex flex-col gap-2" aria-hidden>
          <div className="h-3 w-24 animate-pulse rounded-tag bg-co-line2" />
          <div className="h-3 w-full max-w-[60ch] animate-pulse rounded-tag bg-co-line" />
          <div className="h-3 w-4/5 max-w-[50ch] animate-pulse rounded-tag bg-co-line" />
        </div>
      ) : messages.length === 0 ? (
        <p className="tt-read-m text-co-faint">
          {busy ? "Working. Its reply appears here when it finishes." : "No reply yet."}
        </p>
      ) : (
        <div className="ws-agent-log co-stagger">
          {messages.map((m) => {
            if (m.role === "stop") {
              return (
                <p key={m.id} className="ws-agent-event" role="note">
                  <Square aria-hidden="true" fill="currentColor" strokeWidth={0} />
                  <span>Stopped by {m.stoppedByName?.trim() || "a teammate"}</span>
                  <time dateTime={m.createdAt}>{formatWhen(m.createdAt)}</time>
                </p>
              );
            }
            const yours = m.role === "user";
            const text = m.content.trim();
            return (
              <div key={m.id} className={yours ? "ws-agent-you" : "ws-agent-turn"}>
                {text ? <TalkBubble text={text} side={yours ? "you" : "agent"} /> : null}
                <p className="ws-agent-meta">
                  <time dateTime={m.createdAt}>{formatWhen(m.createdAt)}</time>
                  {m.unread ? <span>Not read yet</span> : null}
                  {m.status === "aborted" ? <span>Stopped</span> : null}
                </p>
                {m.tools.length > 0 ? (
                  <p className="ws-agent-meta">Used {[...new Set(m.tools)].join(", ")}</p>
                ) : null}
              </div>
            );
          })}
          {busy ? (
            <p className="tt-read-s flex items-center gap-2 text-co-faint">
              <Loader2 className="size-3.5 animate-spin" aria-hidden /> Working…
            </p>
          ) : null}
        </div>
      )}
    </section>
  );
}

type Props = {
  projectId: string;
  deployment: WorkshopDeployment | null;
  canWrite: boolean;
  onClose: () => void;
  onOpenArtifact?: (artifactId: string) => void;
};

export default function DeploymentDetailModal({
  projectId,
  deployment: live,
  canWrite,
  onClose,
  onOpenArtifact,
}: Props) {
  // Closing clears `live` at once; the last run stays on screen for the close
  // animation, so the body does not vanish while the header shrinks.
  const [kept, setKept] = useState(live);
  if (live && live !== kept) setKept(live);
  const deployment = live ?? kept;
  const { deleteDeployment } = useWorkshopDeployments(projectId);
  const pinned = useMediaQuery("(min-width: 768px)");
  // The modal grows out of the run it was opened from, and the last open run
  // is kept because closing has already cleared `deployment`.
  const lastId = useRef(live?.id ?? null);
  useLayoutEffect(() => {
    if (live) lastId.current = live.id;
  }, [live]);
  const growFrom = useCallback(() => {
    const id = lastId.current;
    return id ? document.querySelector(`[data-deployment-id="${CSS.escape(id)}"]`) : null;
  }, []);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const runs = useWorkshopDeploymentRuns(deployment?.id ?? null, !!live);
  const artifactsQuery = useWorkshopDeploymentArtifacts(
    deployment?.id ?? null,
    !!live,
    isDeploymentRunBusy(deployment?.lastRunStatus),
  );
  const { data: chatModels = [] } = useChatModels();
  const latestRun = runs.data?.[0] ?? null;
  const artifacts = artifactsQuery.data ?? [];
  const viewerId = useViewerId();
  const deployedBy = formatAttribution(deployment?.attribution?.createdBy, viewerId);
  const agentName =
    deployment?.definitionName ?? deployment?.definitionKey ?? "Agent";
  const title = deployment?.name ?? "Agent run";
  const modelDisplayName = useMemo(() => {
    const id = latestRun?.model;
    if (!id) return null;
    return chatModels.find((m) => m.id === id)?.name ?? id;
  }, [chatModels, latestRun?.model]);
  const running =
    latestRun?.status === "running" || deployment?.lastRunStatus === "running";

  const close = () => {
    setConfirmOpen(false);
    onClose();
  };

  return (
    <>
    <KitModal
      isOpen={live !== null}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      title={title}
      modalClassName="!w-[min(calc(100vw-2rem),720px)] !max-w-[720px]"
      overlayClassName="ws-pinned-overlay"
      pinTop={pinned}
      growFrom={growFrom}
    >
      {deployment ? (
        <div className="flex max-h-[min(72vh,680px)] flex-col">
          <div className="co-notif-in -mt-1 mb-5 flex items-center justify-between gap-3 border-b border-co-line pb-4" style={stagger(0)}>
            <p className="tt-read-s min-w-0 truncate text-co-muted">
              {agentName}
              {deployedBy ? ` · deployed ${deployedBy}` : null}
            </p>
            <div className="flex shrink-0 items-center gap-2">
              <RunStatusChip status={deployment.lastRunStatus} />
              <span className="tt-label tabular-nums text-co-faint">
                {formatWhen(deployment.lastRunAt)}
              </span>
            </div>
          </div>

          <div className="custom-scrollbar flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pr-0.5">
            <section className="co-notif-in flex flex-col gap-1.5" style={stagger(1)}>
              <p className="tt-label font-semibold uppercase text-co-faint">
                Objective
              </p>
              <p className="tt-read-m max-w-[65ch] whitespace-pre-wrap text-co-text2">
                {deployment.objective}
              </p>
            </section>

            {deployment.lastError ? (
              <section className="co-notif-in flex flex-col gap-1.5 bg-co-danger/10 px-3 py-2.5" style={stagger(2)}>
                <p className="tt-label font-semibold uppercase text-co-alert-ink">
                  Error
                </p>
                <p className="tt-read-m whitespace-pre-wrap text-co-alert-ink">
                  {deployment.lastError}
                </p>
              </section>
            ) : null}

            <AgentConversation
              deploymentId={deployment.id}
              busy={isDeploymentRunBusy(deployment.lastRunStatus)}
              index={deployment.lastError ? 3 : 2}
            />

            <section className="co-notif-in flex flex-col gap-2" style={stagger(deployment.lastError ? 4 : 3)}>
              <p className="tt-label font-semibold uppercase text-co-faint">
                Artifacts
                {artifacts.length > 0 ? ` · ${artifacts.length}` : ""}
              </p>
              {artifactsQuery.isLoading ? (
                <div className="tt-read-s flex items-center gap-2 py-1 text-co-faint">
                  <Loader2 className="size-3.5 animate-spin" /> Loading…
                </div>
              ) : artifacts.length === 0 ? (
                <p className="tt-read-m text-co-faint">
                  None written by this run.
                </p>
              ) : (
                <ul className="flex flex-col divide-y divide-co-line overflow-hidden rounded-card border border-co-line2">
                  {artifacts.map((a) => (
                    <li key={a.id}>
                      <button
                        type="button"
                        onClick={() => {
                          onOpenArtifact?.(a.id);
                          close();
                        }}
                        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-start transition-colors hover:bg-co-hover"
                      >
                        <FileText
                          className="size-3.5 shrink-0 text-co-faint"
                          aria-hidden
                        />
                        <span className="tt-read-m min-w-0 flex-1 truncate font-medium text-co-text">
                          {a.title}
                        </span>
                        <span className="tt-label shrink-0 text-co-faint">
                          v{a.version}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          <footer className="co-notif-in mt-5 flex items-end justify-between gap-4 border-t border-co-line pt-3.5" style={stagger(deployment.lastError ? 5 : 4)}>
            <div className="min-w-0 flex-1">
            {runs.isLoading ? (
              <div className="tt-read-s flex items-center gap-2 text-co-faint">
                <Loader2 className="size-3.5 animate-spin" /> Loading run…
              </div>
            ) : latestRun ? (
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  {latestRun.model && modelDisplayName ? (
                    <span className="inline-flex min-w-0 items-center gap-2">
                      <ChatModelProviderMark
                        modelId={latestRun.model}
                        size={18}
                      />
                      <span className="tt-read-m truncate font-medium text-co-text">
                        {modelDisplayName}
                      </span>
                    </span>
                  ) : (
                    <span className="tt-read-m text-co-faint">No model</span>
                  )}

                  <span className="hidden h-3 w-px bg-co-line2 sm:block" aria-hidden />

                  <span
                    className="tt-label inline-flex items-center gap-1.5 tabular-nums text-co-muted"
                    title={`${(latestRun.inputTokens ?? 0).toLocaleString()} input · ${(latestRun.outputTokens ?? 0).toLocaleString()} output`}
                  >
                    <span className="inline-flex items-center gap-0.5">
                      <span aria-hidden className="text-co-faint">
                        ↓
                      </span>
                      <span className="text-co-text2">
                        {formatTokenCount(latestRun.inputTokens)}
                      </span>
                      <span className="sr-only">input</span>
                    </span>
                    <span className="text-co-line2" aria-hidden>
                      ·
                    </span>
                    <span className="inline-flex items-center gap-0.5">
                      <span aria-hidden className="text-co-faint">
                        ↑
                      </span>
                      <span className="text-co-text2">
                        {formatTokenCount(latestRun.outputTokens)}
                      </span>
                      <span className="sr-only">output</span>
                    </span>
                  </span>

                  <span className="hidden h-3 w-px bg-co-line2 sm:block" aria-hidden />

                  <span className="tt-label tabular-nums text-co-faint">
                    {formatWhen(latestRun.finishedAt ?? latestRun.scheduledAt)}
                  </span>
                </div>
                {latestRun.error ? (
                  <p className="tt-read-s text-co-alert-ink">
                    {latestRun.error}
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="tt-read-s text-co-faint">No run recorded yet.</p>
            )}
            </div>
            {canWrite ? (
              <KitButton
                type="button"
                variant="ghost"
                className="shrink-0 text-co-alert-ink hover:bg-co-danger/10"
                onClick={() => setConfirmOpen(true)}
              >
                Delete
              </KitButton>
            ) : null}
          </footer>
        </div>
      ) : null}
    </KitModal>
    <WorkshopDialog
      isOpen={confirmOpen}
      onOpenChange={setConfirmOpen}
      title={running ? "This agent is still running" : "Delete this agent?"}
      description={
        running
          ? "Stop it first, or wait until it finishes. Then you can delete it."
          : `“${title}” leaves this project. Outputs it already wrote stay.`
      }
      consequence={running ? undefined : "This cannot be undone"}
      cancelLabel={running ? "OK" : "Keep agent"}
      confirmLabel={running ? undefined : "Delete agent"}
      confirmTone="danger"
      confirmLoading={deleteDeployment.isPending}
      onConfirm={() => {
        if (!deployment || running) return;
        setConfirmOpen(false);
        deleteDeployment.mutate(deployment.id, {
          onSuccess: close,
        });
      }}
    />
    </>
  );
}
